import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { hvApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import {
  Star, Loader2, MessageSquare, Calendar, Mail,
  X, ChevronLeft, ChevronRight, ChevronDown, Eye, EyeOff, Phone, Trash2,
} from 'lucide-react';
import HVNav from '../components/HVNav';
import HVFooter from '../components/HVFooter';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

// 10-Punkt Stimmungsbarometer
const MOODS = [
  { max: 1.0, label: 'Entsetzt',        color: 'text-red-700',    bg: 'bg-red-100 text-red-700' },
  { max: 1.5, label: 'Sehr verärgert',  color: 'text-red-600',    bg: 'bg-red-100 text-red-600' },
  { max: 2.0, label: 'Unzufrieden',     color: 'text-red-500',    bg: 'bg-red-50 text-red-500' },
  { max: 2.5, label: 'Enttäuscht',      color: 'text-orange-600', bg: 'bg-orange-50 text-orange-600' },
  { max: 3.0, label: 'Neutral',         color: 'text-yellow-600', bg: 'bg-yellow-50 text-yellow-600' },
  { max: 3.5, label: 'Akzeptabel',      color: 'text-yellow-500', bg: 'bg-yellow-50 text-yellow-500' },
  { max: 4.0, label: 'Zufrieden',       color: 'text-green-500',  bg: 'bg-green-50 text-green-500' },
  { max: 4.5, label: 'Erfreut',         color: 'text-green-600',  bg: 'bg-green-50 text-green-600' },
  { max: 5.0, label: 'Begeistert',      color: 'text-green-700',  bg: 'bg-green-100 text-green-700' },
  { max: 6.0, label: 'Begeistert',      color: 'text-green-700',  bg: 'bg-green-100 text-green-700' },
];

function getMood(rating) {
  const r = Math.round(rating * 2) / 2;
  return MOODS.find(m => r <= m.max) || MOODS[MOODS.length - 1];
}

// KI-Stimmung: feste Werteliste (siehe backend/ai.py SENTIMENTS) auf Farben gemappt.
const SENTIMENT_STYLES = {
  'verärgert':   'bg-red-100 text-red-800',
  'enttäuscht':  'bg-orange-100 text-orange-800',
  'frustriert':  'bg-orange-100 text-orange-800',
  'besorgt':     'bg-amber-100 text-amber-800',
  'kritisch':    'bg-yellow-100 text-yellow-800',
  'neutral':     'bg-slate-100 text-slate-700',
  'sachlich':    'bg-slate-100 text-slate-700',
  'zufrieden':   'bg-green-100 text-green-800',
  'dankbar':     'bg-green-100 text-green-800',
  'begeistert':  'bg-emerald-100 text-emerald-800',
};

function SentimentBadge({ review }) {
  const value = review.sentiment;
  if (!value) {
    // Noch keine KI-Stimmung (Bestandsdaten) → Sternzahl als Näherung, sichtbar abgesetzt
    const mood = getMood(review.overall_rating);
    return (
      <span
        className={`text-xs px-2 py-0.5 rounded-full font-medium ${mood.bg} opacity-60`}
        title="Aus der Sternzahl abgeleitet — noch keine KI-Stimmung berechnet"
      >
        {mood.label}
      </span>
    );
  }
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${SENTIMENT_STYLES[value] || 'bg-slate-100 text-slate-700'}`}>
      {value}
    </span>
  );
}

function getFirstWords(text, n = 5) {
  if (!text) return '—';
  return text.trim().split(/\s+/).slice(0, n).join(' ') + (text.trim().split(/\s+/).length > n ? '…' : '');
}

const PAGE_SIZE_OPTIONS = [15, 30, 50, 100];

// Column filter dropdown component
function ColumnHeader({ label, filterKey, filters, setFilter, options, sortable = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const active = filters[filterKey] && filters[filterKey] !== 'all';

  useEffect(() => {
    const handle = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  if (!options) return <TableHead>{label}</TableHead>;

  return (
    <TableHead>
      <div className="relative" ref={ref}>
        <button
          onClick={() => setOpen(o => !o)}
          className={`flex items-center gap-1 hover:text-foreground transition-colors ${active ? 'text-primary font-semibold' : ''}`}
        >
          {label}
          <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
          {active && <span className="w-1.5 h-1.5 rounded-full bg-primary inline-block ml-0.5" />}
        </button>
        {open && (
          <div className="absolute top-full left-0 mt-1 w-44 bg-background border border-border rounded-lg shadow-lg py-1 z-50 text-sm font-normal">
            {options.map(opt => (
              <button
                key={opt.value}
                onClick={() => { setFilter(filterKey, opt.value); setOpen(false); }}
                className={`w-full text-left px-3 py-1.5 hover:bg-muted transition-colors flex items-center justify-between ${filters[filterKey] === opt.value ? 'text-primary font-medium' : 'text-foreground'}`}
              >
                {opt.label}
                {filters[filterKey] === opt.value && <span className="w-1.5 h-1.5 rounded-full bg-primary" />}
              </button>
            ))}
          </div>
        )}
      </div>
    </TableHead>
  );
}

export default function HVReviews() {
  const { user, loading: authLoading } = useAuth('hv_user');
  const [searchParams] = useSearchParams();

  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [links, setLinks] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [selectedReview, setSelectedReview] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);

  const [filters, setFiltersState] = useState({
    link_id: searchParams.get('link_id') || 'all',
    rating: searchParams.get('rating') || 'all',
    answered: searchParams.get('answered') || 'all',
    path: 'all',
    sentiment: 'all',
  });
  // Verfügbare Stimmungen + Häufigkeiten, kommen vom Backend
  const [sentimentFacets, setSentimentFacets] = useState({});

  const [datePreset, setDatePreset] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const applyDatePreset = (preset) => {
    setDatePreset(preset);
    setPage(1);
    const today = new Date();
    const fmt = (d) => d.toISOString().split('T')[0];
    if (preset === 'all')           { setDateFrom(''); setDateTo(''); }
    else if (preset === '7d')       { const d = new Date(today); d.setDate(d.getDate()-6); setDateFrom(fmt(d)); setDateTo(fmt(today)); }
    else if (preset === '14d')      { const d = new Date(today); d.setDate(d.getDate()-13); setDateFrom(fmt(d)); setDateTo(fmt(today)); }
    else if (preset === '1m')       { const d = new Date(today); d.setMonth(d.getMonth()-1); setDateFrom(fmt(d)); setDateTo(fmt(today)); }
    else if (preset === '3m')       { const d = new Date(today); d.setMonth(d.getMonth()-3); setDateFrom(fmt(d)); setDateTo(fmt(today)); }
    else if (preset === '6m')       { const d = new Date(today); d.setMonth(d.getMonth()-6); setDateFrom(fmt(d)); setDateTo(fmt(today)); }
    else if (preset === '2026')     { setDateFrom('2026-01-01'); setDateTo('2026-12-31'); }
    else if (preset === 'custom')   { /* keep existing */ }
  };

  const setFilter = (key, value) => {
    setFiltersState(f => ({ ...f, [key]: value }));
    setPage(1);
  };

  const resetFilters = () => {
    setFiltersState({ link_id: 'all', rating: 'all', answered: 'all', path: 'all', sentiment: 'all' });
    setDatePreset('all');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== 'all') || datePreset !== 'all' || dateFrom || dateTo;

  const loadReviews = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const params = { skip: (page - 1) * pageSize, limit: pageSize };
      if (filters.link_id !== 'all') params.link_id = filters.link_id;
      if (filters.rating !== 'all') params.rating = filters.rating;
      if (filters.answered !== 'all') params.answered = filters.answered;
      if (filters.path !== 'all') params.path = filters.path;
      if (filters.sentiment !== 'all') params.sentiment = filters.sentiment;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const res = await hvApi.getAllReviews(params);
      const data = res.data;
      setReviews(data.reviews || []);
      setTotal(data.total || 0);
      setUnread(data.unanswered || 0);
      setSentimentFacets(data.sentiment_facets || {});
    } catch {
      toast.error('Fehler beim Laden der Bewertungen');
    } finally {
      setLoading(false);
    }
  }, [user, filters, page, pageSize, dateFrom, dateTo]);

  useEffect(() => { loadReviews(); }, [loadReviews]);

  useEffect(() => {
    if (!user) return;
    hvApi.getLinks().then(r => setLinks(r.data)).catch(() => {});
  }, [user]);

  const openReviewDetail = (review) => {
    setSelectedReview(review);
    setSheetOpen(true);
    // Mark as read when opening
    if (!review.answered) markAsRead(review.id, true);
  };

  const markAsRead = async (reviewId, silent = false) => {
    setMarkingRead(true);
    try {
      await hvApi.markReviewAnswered(reviewId);
      setReviews(rs => rs.map(r => r.id === reviewId ? { ...r, answered: true } : r));
      if (selectedReview?.id === reviewId) setSelectedReview(r => ({ ...r, answered: true }));
      setUnread(u => Math.max(0, u - 1));
      if (!silent) toast.success('Als gelesen markiert');
    } catch {
      if (!silent) toast.error('Fehler beim Speichern');
    } finally {
      setMarkingRead(false);
    }
  };

  // Löschung auf Wunsch eines Bewerters (Art. 17 DSGVO) — endgültig
  const deleteReview = async (review) => {
    if (!window.confirm('Diese Bewertung samt E-Mail-Adresse endgültig löschen? Das lässt sich nicht rückgängig machen.')) return;
    try {
      await hvApi.deleteReview(review.id);
      setReviews(rs => rs.filter(r => r.id !== review.id));
      setTotal(t => Math.max(0, t - 1));
      if (!review.answered) setUnread(u => Math.max(0, u - 1));
      setSheetOpen(false);
      setSelectedReview(null);
      toast.success('Bewertung gelöscht');
    } catch {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const markAsUnread = async (reviewId) => {
    try {
      await hvApi.markReviewUnread(reviewId);
      setReviews(rs => rs.map(r => r.id === reviewId ? { ...r, answered: false } : r));
      if (selectedReview?.id === reviewId) setSelectedReview(r => ({ ...r, answered: false }));
      setUnread(u => u + 1);
      toast.success('Als ungelesen markiert');
    } catch {
      toast.error('Fehler beim Speichern');
    }
  };

  const renderStars = (rating, colored = false) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => {
        let cls = 'w-4 h-4 ';
        if (i <= rating) {
          if (colored) cls += rating >= 4 ? 'fill-green-500 text-green-500' : 'fill-red-500 text-red-500';
          else cls += 'fill-[hsl(var(--rating-gold))] text-[hsl(var(--rating-gold))]';
        } else {
          cls += 'text-gray-300';
        }
        return <Star key={i} className={cls} />;
      })}
    </div>
  );

  const totalPages = Math.ceil(total / pageSize);

  // KPIs
  const positiveCount = reviews.filter(r => r.overall_rating >= 4).length;
  const criticalCount = reviews.filter(r => r.overall_rating <= 3).length;
  const avgRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.overall_rating, 0) / reviews.length).toFixed(1)
    : null;

  // Stacked bar data
  const stackedTotal = positiveCount + criticalCount;
  const positivePercent = stackedTotal > 0 ? (positiveCount / stackedTotal * 100).toFixed(0) : 50;
  const criticalPercent = stackedTotal > 0 ? (criticalCount / stackedTotal * 100).toFixed(0) : 50;

  // Column filter options
  const ratingOptions = [
    { value: 'all', label: 'Alle Bewertungen' },
    { value: '5', label: '5 Sterne' },
    { value: '4', label: '4 Sterne' },
    { value: '3', label: '3 Sterne' },
    { value: '2', label: '2 Sterne' },
    { value: '1', label: '1 Stern' },
  ];
  const linkOptions = [
    { value: 'all', label: 'Alle Links' },
    ...links.map(l => ({ value: l.id, label: l.name })),
  ];
  const readOptions = [
    { value: 'all', label: 'Alle' },
    { value: 'false', label: 'Ungelesen' },
    { value: 'true', label: 'Gelesen' },
  ];
  const dateOptions = [
    { value: 'all', label: 'Alle Zeiträume' },
    { value: '7d', label: 'Letzte 7 Tage' },
    { value: '14d', label: 'Letzte 14 Tage' },
    { value: '1m', label: 'Letzter Monat' },
    { value: '3m', label: 'Letzte 3 Monate' },
    { value: '6m', label: 'Letzte 6 Monate' },
    { value: '2026', label: '2026' },
  ];

  if (authLoading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <HVNav unreadReviews={unread} />

      <main className="container-main py-8">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold mb-1">Alle Bewertungen</h1>
            <p className="text-muted-foreground text-sm">
              Überblick über alle Bewertungen aus allen Links
            </p>
          </div>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={resetFilters}>
              <X className="w-4 h-4 mr-1" />
              Filter zurücksetzen
            </Button>
          )}
        </div>

        {/* KPI + Stacked Bar */}
        <div className="mb-6 space-y-3">
          {/* Stacked bar */}
          {stackedTotal > 0 && (
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-4 mb-2 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm bg-green-500 inline-block" />
                    <span className="text-sm text-muted-foreground">Positiv (4–5★)</span>
                    <span className="text-sm font-bold text-green-600">{positiveCount}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm bg-red-500 inline-block" />
                    <span className="text-sm text-muted-foreground">Kritisch (1–3★)</span>
                    <span className="text-sm font-bold text-red-500">{criticalCount}</span>
                  </div>
                  <div className="ml-auto flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">Gesamt: <strong className="text-foreground">{total}</strong></span>
                    {avgRating && <span className="text-sm text-muted-foreground">Ø <strong className="text-foreground">{avgRating}★</strong></span>}
                    {unread > 0 && (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
                        {unread} ungelesen
                      </span>
                    )}
                  </div>
                </div>
                <div className="h-4 rounded-full overflow-hidden flex gap-0.5">
                  <div
                    className="h-full bg-green-500 rounded-l-full transition-all"
                    style={{ width: `${positivePercent}%` }}
                    title={`${positiveCount} Positiv (${positivePercent}%)`}
                  />
                  <div
                    className="h-full bg-red-500 rounded-r-full transition-all"
                    style={{ width: `${criticalPercent}%` }}
                    title={`${criticalCount} Kritisch (${criticalPercent}%)`}
                  />
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Date filter row */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <Select value={datePreset} onValueChange={applyDatePreset}>
            <SelectTrigger className="w-44 h-9 text-sm">
              <SelectValue placeholder="Zeitraum" />
            </SelectTrigger>
            <SelectContent>
              {dateOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              <SelectItem value="custom">Benutzerdefiniert</SelectItem>
            </SelectContent>
          </Select>
          {datePreset === 'custom' && (
            <>
              <Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} className="w-[140px] h-9 text-sm" />
              <span className="text-sm text-muted-foreground">–</span>
              <Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} className="w-[140px] h-9 text-sm" />
            </>
          )}
        </div>

        {/* Reviews Table */}
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle className="text-lg">Bewertungen</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : reviews.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                <p className="text-muted-foreground">
                  {hasActiveFilters ? 'Keine Bewertungen für diesen Filter' : 'Noch keine Bewertungen vorhanden'}
                </p>
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={resetFilters} className="mt-2">
                    Filter zurücksetzen
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto -mx-4 sm:mx-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <ColumnHeader
                      label="Datum"
                      filterKey="date"
                      filters={{ date: datePreset }}
                      setFilter={(_, v) => applyDatePreset(v)}
                      options={dateOptions}
                    />
                    <ColumnHeader
                      label="Bewertung"
                      filterKey="rating"
                      filters={filters}
                      setFilter={setFilter}
                      options={ratingOptions}
                    />
                    <ColumnHeader
                      label="Stimmung"
                      filterKey="sentiment"
                      filters={filters}
                      setFilter={setFilter}
                      options={[
                        { value: 'all', label: 'Alle' },
                        ...Object.entries(sentimentFacets)
                          .sort((a, b) => b[1] - a[1])
                          .map(([value, count]) => ({ value, label: `${value} (${count})` })),
                      ]}
                    />
                    <TableHead>Kontext</TableHead>
                    <ColumnHeader
                      label="Link"
                      filterKey="link_id"
                      filters={filters}
                      setFilter={setFilter}
                      options={linkOptions}
                    />
                    <TableHead>Kontakt</TableHead>
                    <ColumnHeader
                      label="Gelesen"
                      filterKey="answered"
                      filters={filters}
                      setFilter={setFilter}
                      options={readOptions}
                    />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reviews.map((review) => {
                    const isUnread = !review.answered;
                    const hasContact = review.reaction_gate?.chosen === 'send_to_hv' || review.verification?.email;
                    return (
                      <TableRow
                        key={review.id}
                        className={`cursor-pointer hover:bg-muted/50 transition-colors ${isUnread ? 'font-semibold' : ''}`}
                        onClick={() => openReviewDetail(review)}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
                            <span className="text-sm">
                              {review.createdAt
                                ? format(new Date(review.createdAt), 'dd.MM.yy', { locale: de })
                                : '-'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>{renderStars(review.overall_rating, true)}</TableCell>
                        <TableCell>
                          <SentimentBadge review={review} />
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {getFirstWords(review.free_text || review.suggestion_text)}
                          </span>
                        </TableCell>
                        <TableCell>
                          {review.link_name ? (
                            <Badge variant="secondary" className="text-xs font-normal">{review.link_name}</Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {hasContact ? (
                            <span title={review.verification?.email || 'Kontakt gewünscht'}>
                              <Mail className="w-4 h-4 text-primary" />
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {isUnread ? (
                            <span className="flex items-center gap-1 text-xs text-muted-foreground">
                              <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />
                              Neu
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Eye className="w-3.5 h-3.5" />
                              Gelesen
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </div>
            )}

            {/* Pagination */}
            <div className="flex items-center justify-between pt-4 border-t mt-4 flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Einträge pro Seite:</span>
                <Select value={String(pageSize)} onValueChange={v => { setPageSize(Number(v)); setPage(1); }}>
                  <SelectTrigger className="w-20 h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map(n => (
                      <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-sm text-muted-foreground">
                Seite {page} von {totalPages || 1} · {total} Einträge
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
                  <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                  Weiter <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Review Detail Sheet */}
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Bewertungsdetails</SheetTitle>
            </SheetHeader>
            {selectedReview && (
              <div className="mt-6 space-y-5">

                {/* Impuls vs. differenziert + Stimmung */}
                {(() => {
                  const cats = selectedReview.categories || [];
                  const refined = cats.length
                    ? cats.reduce((s, c) => s + (c.rating || 0), 0) / cats.length
                    : null;
                  const delta = refined != null ? refined - selectedReview.overall_rating : null;
                  const fmt = (n) => n.toFixed(1).replace('.', ',');
                  return (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 rounded-lg bg-muted/50 text-center">
                        <p className="text-xs text-muted-foreground mb-2">Impulsbewertung</p>
                        <div className="flex justify-center mb-1">
                          {renderStars(selectedReview.overall_rating, true)}
                        </div>
                        <p className="text-2xl font-bold">
                          {selectedReview.overall_rating}<span className="text-sm text-muted-foreground"> von 5</span>
                        </p>
                      </div>
                      <div className="p-4 rounded-lg bg-muted/50 text-center">
                        <p className="text-xs text-muted-foreground mb-2">Differenziert</p>
                        {refined != null ? (
                          <>
                            <p className="text-2xl font-bold">
                              {fmt(refined)}<span className="text-sm text-muted-foreground"> von 5</span>
                            </p>
                            {Math.abs(delta) >= 0.05 && (
                              <p className={`text-xs font-medium mt-1 ${delta > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {delta > 0 ? '↑' : '↓'} {fmt(Math.abs(delta))} gegenüber Impuls
                              </p>
                            )}
                            {selectedReview.overall_rating < 4 && refined >= 4 && (
                              <p className="text-[11px] text-green-700 font-semibold mt-1">
                                kritisch → positiv
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="text-sm text-muted-foreground mt-3">Keine Detailangaben</p>
                        )}
                      </div>
                    </div>
                  );
                })()}
                <div className="grid grid-cols-1">
                  <div className="p-4 rounded-lg bg-muted/50 text-center flex flex-col items-center justify-center">
                    <p className="text-xs text-muted-foreground mb-2">Stimmung</p>
                    {selectedReview.sentiment ? (
                      <span className={`px-3 py-1.5 rounded-full text-sm font-semibold ${SENTIMENT_STYLES[selectedReview.sentiment] || 'bg-slate-100 text-slate-700'}`}>
                        {selectedReview.sentiment}
                      </span>
                    ) : (() => {
                      const mood = getMood(selectedReview.overall_rating);
                      return (
                        <span
                          className={`px-3 py-1.5 rounded-full text-sm font-semibold ${mood.bg} opacity-60`}
                          title="Aus der Sternzahl abgeleitet — noch keine KI-Stimmung berechnet"
                        >
                          {mood.label}
                        </span>
                      );
                    })()}
                  </div>
                </div>

                {/* Datum */}
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Datum</p>
                  <p className="text-sm font-medium">
                    {selectedReview.createdAt
                      ? format(new Date(selectedReview.createdAt), 'dd. MMMM yyyy, HH:mm', { locale: de }) + ' Uhr'
                      : '—'}
                  </p>
                </div>

                {/* Bewertungstext */}
                {(selectedReview.free_text || selectedReview.suggestion_text) && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Bewertungstext</p>
                    <p className="p-3 rounded-lg bg-muted text-sm leading-relaxed">
                      {selectedReview.free_text || selectedReview.suggestion_text}
                    </p>
                  </div>
                )}

                {/* Umformulierter Text */}
                {selectedReview.rewritten_text && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Sachlich umformuliert</p>
                    <p className="p-3 rounded-lg bg-green-50 border border-green-200 text-sm leading-relaxed">
                      {selectedReview.rewritten_text}
                    </p>
                  </div>
                )}

                {/* Link */}
                {selectedReview.link_name && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Link</p>
                    <Badge variant="secondary">{selectedReview.link_name}</Badge>
                  </div>
                )}

                {/* Kategorien */}
                {selectedReview.categories?.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Kategorien</p>
                    <div className="space-y-1.5">
                      {selectedReview.categories.map((cat, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="text-sm">{cat.label}</span>
                          {renderStars(cat.rating, true)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sachlichkeitsanalyse */}
                {selectedReview.fairness && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Sachlichkeitsanalyse</p>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="p-2 rounded bg-muted">
                        <p className="text-lg font-bold">{selectedReview.fairness.fairness_score}%</p>
                        <p className="text-xs text-muted-foreground">Sachlichkeit</p>
                      </div>
                      <div className="p-2 rounded bg-muted">
                        <p className="text-lg font-bold">{selectedReview.fairness.emotion_score}%</p>
                        <p className="text-xs text-muted-foreground">Emotion</p>
                      </div>
                      <div className="p-2 rounded bg-muted">
                        <p className="text-lg font-bold">{selectedReview.fairness.risk_score}%</p>
                        <p className="text-xs text-muted-foreground">Risiko</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* KI-Regenerierungen */}
                {selectedReview.regeneration_count > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">KI-Anpassungen</p>
                    <p className="text-sm">Der Bewerter hat den Text <strong>{selectedReview.regeneration_count}×</strong> neu generiert.</p>
                  </div>
                )}

                {/* Kontakt / E-Mail */}
                {selectedReview.verification?.email && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Kontakt</p>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">{selectedReview.verification.email}</span>
                    </div>
                  </div>
                )}

                {/* Kontaktwunsch */}
                {selectedReview.reaction_gate?.chosen && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Rückmeldewunsch</p>
                    <p className="text-sm">
                      {selectedReview.reaction_gate.chosen === 'send_to_hv'
                        ? '✓ Bewerter wünscht Rückmeldung der Hausverwaltung'
                        : 'Keine Rückmeldung gewünscht'}
                    </p>
                  </div>
                )}

                {/* Gelesen/Ungelesen Buttons */}
                <div className="flex gap-2 pt-2">
                  {selectedReview.answered ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => markAsUnread(selectedReview.id)}
                      className="flex-1"
                    >
                      <EyeOff className="w-4 h-4 mr-2" />
                      Als ungelesen markieren
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => markAsRead(selectedReview.id)}
                      disabled={markingRead}
                      className="flex-1"
                    >
                      {markingRead ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                      Als gelesen markieren
                    </Button>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteReview(selectedReview)}
                  className="w-full text-destructive hover:text-destructive"
                  data-testid="hv-review-delete-button"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Bewertung löschen (z. B. auf Wunsch des Bewerters)
                </Button>
              </div>
            )}
          </SheetContent>
        </Sheet>

        <HVFooter />
      </main>
    </div>
  );
}
